// src/pages/admin/adminProfile.tsx
//
// "Your Profile" in AdminHeader.tsx navigates to /adminProfile, but no page
// component or route existed for it, so the page rendered blank. This page
// fixes that: it shows the logged-in user's info (works for both Admin and
// Vendor sessions, since AdminHeader is shared between the two) and lets
// them change their password.
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Card, Typography, TextField, Button, Alert, CircularProgress,
  InputAdornment, IconButton, Chip, Divider, Avatar, Drawer,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControl,
  InputLabel, Select, MenuItem, List, ListItem, ListItemText,
  ListItemSecondaryAction, Tooltip,
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import AdminHeader from '../../components/admin/adminHeader.tsx';
import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import {
  useListWarehousesQuery,
  useAddWarehouseMutation,
  useUpdateWarehouseMutation,
  useDeleteWarehouseMutation,
  Warehouse,
  WarehouseFormData,
  LOCATION_TYPE_OPTIONS,
  LocationType,
  STATUS_OPTIONS,
} from '../../features/admin/warehouseApi.ts';

const AUTH_KEY = '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3';

const normalizeRole = (role: string): string => {
  const r = role?.toLowerCase().trim() || '';
  if (!r || r === 'new' || r === 'unknown') return 'admin';
  if (r.includes('super')) return 'super_admin';
  if (r === 'operation' || r === 'vendor') return r;
  return 'admin';
};

const roleColor = (normalized: string): string => {
  switch (normalized) {
    case 'super_admin': return 'var(--orange-dark, #e94c1c)';
    case 'admin':       return '#2196f3';
    case 'operation':   return '#ff9800';
    case 'vendor':      return '#4caf50';
    default:            return 'var(--orange, #F76338)';
  }
};

const AdminProfile: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  // vendorlogin.tsx redirects here (instead of straight to Product
  // Management) when a vendor has no pickup address on file yet, or when
  // they choose "Add new address" from the post-login prompt. In both
  // cases we jump straight into the Add Pickup Location dialog so they
  // aren't left guessing where to go.
  const addressSetupState = (location.state || {}) as { openAddressDialog?: boolean; addressPrompt?: string };

  const isVendor   = (localStorage.getItem('user_role') || '').toLowerCase() === 'vendor';
  const userName   = (isVendor ? localStorage.getItem('user_name')  : localStorage.getItem('admin_name'))  || 'Admin';
  const userEmail  = (isVendor ? localStorage.getItem('user_email') : localStorage.getItem('admin_email')) || '';
  const rawRole    = localStorage.getItem('role') || localStorage.getItem('user_role') || 'admin';
  const normalized = normalizeRole(rawRole);
  const displayRole = rawRole.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  const avatarLetter = (userName.charAt(0) || userEmail.charAt(0) || 'A').toUpperCase();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // ---------- Warehouse / Pickup Location (vendor only) ----------
  const {
    data: warehouseData,
    isLoading: warehousesLoading,
    refetch: refetchWarehouses,
  } = useListWarehousesQuery(undefined, { skip: !isVendor });
  const warehouses: Warehouse[] = warehouseData?.warehouses || [];

  const [addWarehouse, { isLoading: isAddingWarehouse }] = useAddWarehouseMutation();
  const [updateWarehouse, { isLoading: isUpdatingWarehouse }] = useUpdateWarehouseMutation();
  const [deleteWarehouse] = useDeleteWarehouseMutation();

  const [warehouseDialogOpen, setWarehouseDialogOpen] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState<Warehouse | null>(null);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [warehouseForm, setWarehouseForm] = useState<WarehouseFormData>({
    name: '',
    latitude: '',
    longitude: '',
    street: '',
    city: '',
    state: '',
    postal_code: '',
    location_type: 'warehouse',
    status: 1,
    is_default: 0,
  });
  const [warehouseFormErrors, setWarehouseFormErrors] = useState<{ [key: string]: string }>({});
  const isEditingWarehouse = Boolean(editingWarehouse);
  const isWarehouseFormBusy = isAddingWarehouse || isUpdatingWarehouse;

  const resetWarehouseForm = () => {
    setWarehouseForm({
      name: '',
      latitude: '',
      longitude: '',
      street: '',
      city: '',
      state: '',
      postal_code: '',
      location_type: 'warehouse',
      status: 1,
      is_default: 0,
    });
    setWarehouseFormErrors({});
    setEditingWarehouse(null);
  };

  const handleOpenAddWarehouse = () => {
    resetWarehouseForm();
    setWarehouseDialogOpen(true);
  };

  const handleOpenEditWarehouse = (warehouse: Warehouse) => {
    setWarehouseForm({
      name: warehouse.name || '',
      latitude: warehouse.latitude || '',
      longitude: warehouse.longitude || '',
      street: warehouse.street || '',
      city: warehouse.city || '',
      state: warehouse.state || '',
      postal_code: warehouse.postal_code || '',
      location_type: (warehouse.location_type as LocationType) || 'warehouse',
      status: warehouse.status || 1,
      id: warehouse.id,
      is_default: warehouse.is_default ?? 0,
    });
    setEditingWarehouse(warehouse);
    setWarehouseDialogOpen(true);
  };

  const handleCloseWarehouseDialog = () => {
    setWarehouseDialogOpen(false);
    resetWarehouseForm();
  };

  const handleDetectLocation = () => {
    if (!navigator.geolocation) {
      setSuccessMsg('');
      setErrorMsg('Location detection is not supported on this browser. Please enter coordinates manually.');
      return;
    }
    setIsDetectingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        const lat = latitude.toFixed(6);
        const lon = longitude.toFixed(6);

        setWarehouseForm((prev) => ({
          ...prev,
          latitude: lat,
          longitude: lon,
        }));
        setWarehouseFormErrors((prev) => ({ ...prev, latitude: '', longitude: '' }));

        // Reverse-geocode the coordinates into street/city/state/postal code
        // so the person doesn't have to type them in by hand.
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lon}&addressdetails=1`,
            { headers: { Accept: 'application/json' } }
          );
          const data = await res.json();
          const addr = data?.address || {};

          const streetParts = [addr.house_number, addr.road || addr.pedestrian || addr.suburb]
            .filter(Boolean)
            .join(' ');
          const city =
            addr.city || addr.town || addr.village || addr.municipality || addr.county || '';
          const state = addr.state || addr.state_district || '';
          const postalCode = addr.postcode || '';

          setWarehouseForm((prev) => ({
            ...prev,
            street: streetParts || prev.street,
            city: city || prev.city,
            state: state || prev.state,
            postal_code: postalCode || prev.postal_code,
          }));
          setWarehouseFormErrors((prev) => ({
            ...prev,
            street: streetParts ? '' : prev.street,
            city: city ? '' : prev.city,
            state: state ? '' : prev.state,
            postal_code: postalCode ? '' : prev.postal_code,
          }));

          if (!streetParts && !city && !state && !postalCode) {
            setErrorMsg('Got your coordinates, but could not auto-fill the address — please fill Street/City/State/Postal Code manually.');
          } else {
            setErrorMsg('');
          }
        } catch {
          setErrorMsg('Got your coordinates, but could not auto-fill the address — please fill Street/City/State/Postal Code manually.');
        } finally {
          setIsDetectingLocation(false);
        }
      },
      () => {
        setIsDetectingLocation(false);
        setErrorMsg('Could not detect your location. Please enter coordinates manually.');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const validateWarehouseForm = () => {
    const errors: { [key: string]: string } = {};
    if (!warehouseForm.name.trim()) errors.name = 'Name is required';
    if (!warehouseForm.street?.trim()) errors.street = 'Street is required';
    if (!warehouseForm.city?.trim()) errors.city = 'City is required';
    if (!warehouseForm.state?.trim()) errors.state = 'State is required';
    if (!warehouseForm.postal_code?.trim()) errors.postal_code = 'Postal code is required';
    if (!warehouseForm.latitude?.trim()) errors.latitude = 'Latitude is required';
    if (!warehouseForm.longitude?.trim()) errors.longitude = 'Longitude is required';
    setWarehouseFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveWarehouse = async () => {
    if (!validateWarehouseForm()) return;
    try {
      if (isEditingWarehouse && editingWarehouse) {
        await updateWarehouse({
          id: warehouseForm.id || editingWarehouse.id,
          name: warehouseForm.name.trim(),
          latitude: warehouseForm.latitude.trim(),
          longitude: warehouseForm.longitude.trim(),
          street: warehouseForm.street?.trim() || '',
          city: warehouseForm.city?.trim() || '',
          state: warehouseForm.state?.trim() || '',
          postal_code: warehouseForm.postal_code?.trim() || '',
          location_type: warehouseForm.location_type || 'warehouse',
          status: warehouseForm.status,
          is_default: warehouseForm.is_default ?? 0,
        }).unwrap();
        setSuccessMsg('Pickup location updated successfully!');
      } else {
        await addWarehouse({
          name: warehouseForm.name.trim(),
          latitude: warehouseForm.latitude.trim(),
          longitude: warehouseForm.longitude.trim(),
          street: warehouseForm.street?.trim() || undefined,
          city: warehouseForm.city?.trim() || undefined,
          state: warehouseForm.state?.trim() || undefined,
          postal_code: warehouseForm.postal_code?.trim() || undefined,
          location_type: warehouseForm.location_type || 'warehouse',
          status: warehouseForm.status,
          is_default: warehouseForm.is_default ?? 0,
        }).unwrap();
        setSuccessMsg('Pickup location added successfully!');
      }
      setErrorMsg('');
      handleCloseWarehouseDialog();
      refetchWarehouses();
    } catch (err: any) {
      setErrorMsg(err?.data?.message || err?.message || 'Failed to save pickup location. Please try again.');
    }
  };

  const handleDeleteWarehouse = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this pickup location?')) return;
    try {
      await deleteWarehouse({ id }).unwrap();
      setSuccessMsg('Pickup location deleted.');
      setErrorMsg('');
      refetchWarehouses();
    } catch (err: any) {
      setErrorMsg(err?.data?.message || err?.message || 'Failed to delete pickup location.');
    }
  };

  const getLocationTypeLabel = (type?: string) =>
    LOCATION_TYPE_OPTIONS.find((opt) => opt.value === type)?.label || 'Warehouse';

  // Auto-open the Add Pickup Location dialog when we arrived here from the
  // post-login address prompt, and clear the navigation state afterwards so
  // refreshing the page doesn't keep reopening it. Reads addressSetupState
  // via a ref-like one-time check so this intentionally only runs on mount.
  const [hasCheckedAddressSetup] = useState(() => addressSetupState.openAddressDialog);
  useEffect(() => {
    if (isVendor && hasCheckedAddressSetup) {
      setWarehouseDialogOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [isVendor, hasCheckedAddressSetup, navigate, location.pathname]);

  const handleChangePassword = async () => {
    setErrorMsg('');
    setSuccessMsg('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setErrorMsg('All fields are required');
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMsg('New passwords do not match');
      return;
    }

    const BASE_URL = (process.env.REACT_APP_SERVER_ENDPOINT || '').replace(/\/$/, '');
    const token = isVendor ? localStorage.getItem('vendor_token') : localStorage.getItem('admin_token');
    const url = isVendor
      ? `${BASE_URL}/user/vendorchange-password`
      : `${BASE_URL}/admin/change-password`;

    setSaving(true);
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          authkey: AUTH_KEY,
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
          confirm_password: confirmPassword,
        }),
      });
      const result = await response.json();
      if (!response.ok || result.error) {
        setErrorMsg(result.message || 'Failed to change password');
      } else {
        setSuccessMsg(result.message || 'Password changed successfully');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      }
    } catch (err) {
      setErrorMsg('Network error: Unable to connect to server');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'var(--light-gray, #fafafa)' }}>
      {/* Desktop Sidebar */}
      <Box sx={{ width: '280px', flexShrink: 0, overflowY: 'auto', display: { xs: 'none', sm: 'block' } }}>
        <Slidemenu mobileOpen={false} onToggle={() => {}} />
      </Box>

      {/* Mobile Sidebar */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { width: 250 } }}
      >
        <Slidemenu mobileOpen={false} onToggle={() => {}} />
      </Drawer>

      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <AdminHeader onToggleMenu={() => setMobileOpen(o => !o)} />

        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 720, mx: 'auto', width: '100%' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>Your Profile</Typography>

          {addressSetupState.openAddressDialog && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {addressSetupState.addressPrompt || 'Please add a pickup address (warehouse, shop, or home) to continue.'}
            </Alert>
          )}
          {errorMsg && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMsg('')}>{errorMsg}</Alert>}
          {successMsg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>{successMsg}</Alert>}

          <Card sx={{ p: 3, borderRadius: '12px', mb: 3, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Avatar sx={{ width: 64, height: 64, bgcolor: roleColor(normalized), fontSize: 28, fontWeight: 700 }}>
                {avatarLetter}
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{userName}</Typography>
                <Typography variant="body2" color="text.secondary">{userEmail}</Typography>
                <Chip
                  label={displayRole}
                  size="small"
                  sx={{
                    mt: 0.5,
                    bgcolor: roleColor(normalized),
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                  }}
                />
              </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <PersonOutlineIcon sx={{ color: '#6d7175', fontSize: 20 }} />
              <Box>
                <Typography variant="caption" color="text.secondary">Full Name</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{userName}</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <PersonOutlineIcon sx={{ color: '#6d7175', fontSize: 20, opacity: 0 }} />
              <Box>
                <Typography variant="caption" color="text.secondary">Email</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{userEmail}</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <PersonOutlineIcon sx={{ color: '#6d7175', fontSize: 20, opacity: 0 }} />
              <Box>
                <Typography variant="caption" color="text.secondary">Role</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{displayRole}</Typography>
              </Box>
            </Box>
          </Card>

          {isVendor && (
            <Card sx={{ p: 3, borderRadius: '12px', mb: 3, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarehouseIcon sx={{ color: '#6d7175' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Pickup Addresses</Typography>
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleOpenAddWarehouse}
                  sx={{ textTransform: 'none', bgcolor: '#202223', '&:hover': { bgcolor: '#000' } }}
                >
                  Add Address
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Add, edit, or remove the warehouse, shop, or home addresses delivery agents will pick up products from. Products you add need at least one pickup address on file.
              </Typography>

              {warehousesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : warehouses.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                  <Typography variant="body2">No pickup address added yet.</Typography>
                </Box>
              ) : (
                <List sx={{ p: 0 }}>
                  {warehouses.map((wh) => (
                    <ListItem
                      key={wh.id}
                      sx={{
                        border: '1px solid #eee',
                        borderRadius: '8px',
                        mb: 1.5,
                        alignItems: 'flex-start',
                        pr: 12,
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>{wh.name}</Typography>
                            <Chip label={getLocationTypeLabel(wh.location_type)} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                            {Number(wh.is_default) === 1 && (
                              <Chip
                                icon={<StarIcon sx={{ fontSize: 14 }} />}
                                label="Default"
                                size="small"
                                color="warning"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                            {Number(wh.status) !== 1 && (
                              <Chip label="Inactive" size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                            )}
                          </Box>
                        }
                        secondary={
                          <>
                            {wh.address && (
                              <Typography variant="body2" color="text.secondary">{wh.address}</Typography>
                            )}
                            <Typography variant="caption" color="text.secondary">
                              {wh.latitude}, {wh.longitude}
                            </Typography>
                          </>
                        }
                      />
                      <ListItemSecondaryAction sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleOpenEditWarehouse(wh)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDeleteWarehouse(wh.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Card>
          )}

          <Card sx={{ p: 3, borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <LockOutlinedIcon sx={{ color: '#6d7175' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Change Password</Typography>
            </Box>

            <TextField
              fullWidth
              label="Current Password"
              type={showCurrent ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowCurrent(s => !s)} edge="end" size="small">
                      {showCurrent ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="New Password"
              type={showNew ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              helperText="8+ characters with upper, lower, number, and special character"
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowNew(s => !s)} edge="end" size="small">
                      {showNew ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Confirm New Password"
              type={showNew ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              sx={{ mb: 3 }}
            />

            <Button
              variant="contained"
              disabled={saving}
              onClick={handleChangePassword}
              sx={{ textTransform: 'none', bgcolor: '#202223', '&:hover': { bgcolor: '#000' } }}
            >
              {saving ? <CircularProgress size={20} color="inherit" /> : 'Update Password'}
            </Button>
          </Card>
        </Box>
      </Box>

      {/* Add / Edit Pickup Location dialog */}
      <Dialog open={warehouseDialogOpen} onClose={handleCloseWarehouseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {isEditingWarehouse ? 'Edit Pickup Address' : 'Add Pickup Address'}
          <IconButton size="small" onClick={handleCloseWarehouseDialog}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Name"
              placeholder="e.g. Main Warehouse, My Shop, Home"
              value={warehouseForm.name}
              onChange={(e) => setWarehouseForm((prev) => ({ ...prev, name: e.target.value }))}
              error={Boolean(warehouseFormErrors.name)}
              helperText={warehouseFormErrors.name}
              fullWidth
              required
            />

            <FormControl fullWidth>
              <InputLabel id="location-type-label">Location Type</InputLabel>
              <Select
                labelId="location-type-label"
                label="Location Type"
                value={warehouseForm.location_type || 'warehouse'}
                onChange={(e) => setWarehouseForm((prev) => ({ ...prev, location_type: e.target.value as LocationType }))}
              >
                {LOCATION_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Street"
              placeholder="Door no., street name, area"
              value={warehouseForm.street || ''}
              onChange={(e) => setWarehouseForm((prev) => ({ ...prev, street: e.target.value }))}
              error={Boolean(warehouseFormErrors.street)}
              helperText={warehouseFormErrors.street}
              fullWidth
              multiline
              minRows={2}
              required
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="City"
                value={warehouseForm.city || ''}
                onChange={(e) => setWarehouseForm((prev) => ({ ...prev, city: e.target.value }))}
                error={Boolean(warehouseFormErrors.city)}
                helperText={warehouseFormErrors.city}
                fullWidth
                required
              />
              <TextField
                label="State"
                value={warehouseForm.state || ''}
                onChange={(e) => setWarehouseForm((prev) => ({ ...prev, state: e.target.value }))}
                error={Boolean(warehouseFormErrors.state)}
                helperText={warehouseFormErrors.state}
                fullWidth
                required
              />
            </Box>

            <TextField
              label="Postal Code"
              value={warehouseForm.postal_code || ''}
              onChange={(e) => setWarehouseForm((prev) => ({ ...prev, postal_code: e.target.value }))}
              error={Boolean(warehouseFormErrors.postal_code)}
              helperText={warehouseFormErrors.postal_code}
              fullWidth
              required
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Latitude"
                value={warehouseForm.latitude}
                onChange={(e) => setWarehouseForm((prev) => ({ ...prev, latitude: e.target.value }))}
                error={Boolean(warehouseFormErrors.latitude)}
                helperText={warehouseFormErrors.latitude}
                fullWidth
                required
              />
              <TextField
                label="Longitude"
                value={warehouseForm.longitude}
                onChange={(e) => setWarehouseForm((prev) => ({ ...prev, longitude: e.target.value }))}
                error={Boolean(warehouseFormErrors.longitude)}
                helperText={warehouseFormErrors.longitude}
                fullWidth
                required
              />
            </Box>

            <Button
              variant="outlined"
              size="small"
              startIcon={isDetectingLocation ? <CircularProgress size={14} /> : <GpsFixedIcon />}
              onClick={handleDetectLocation}
              disabled={isDetectingLocation}
              sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
            >
              {isDetectingLocation ? 'Detecting location...' : 'Use my current location'}
            </Button>

            <FormControl fullWidth>
              <InputLabel id="warehouse-status-label">Status</InputLabel>
              <Select
                labelId="warehouse-status-label"
                label="Status"
                value={warehouseForm.status}
                onChange={(e) => setWarehouseForm((prev) => ({ ...prev, status: Number(e.target.value) }))}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant={warehouseForm.is_default ? 'contained' : 'outlined'}
              size="small"
              startIcon={warehouseForm.is_default ? <StarIcon /> : <StarBorderIcon />}
              onClick={() => setWarehouseForm((prev) => ({ ...prev, is_default: prev.is_default ? 0 : 1 }))}
              sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
            >
              {warehouseForm.is_default ? 'Default pickup address' : 'Set as default pickup address'}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseWarehouseDialog} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveWarehouse}
            disabled={isWarehouseFormBusy}
            sx={{ textTransform: 'none', bgcolor: '#202223', '&:hover': { bgcolor: '#000' } }}
          >
            {isWarehouseFormBusy ? <CircularProgress size={20} color="inherit" /> : (isEditingWarehouse ? 'Save Changes' : 'Add Address')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminProfile;